import React, { ReactNode, useState, useEffect, ChangeEvent } from 'react';
import classNames from 'classnames';
import { Dropdown as RBDropdown, Form, InputGroup, OverlayTrigger, Popover } from 'react-bootstrap';
import { Row } from 'react-table';
import { cloneDeep, isEqual, orderBy } from 'lodash';
import DatePicker from 'react-datepicker';
import { Button, FormCheckbox, FormInput } from '@praktio/common-ui';

import {
  applyFilter,
  capStringAtNumChars,
  dateFilterTypeIsRange,
  filterTypeIsDate,
  returnUniqueValuesInColumn,
} from '../../../utils/functions';

import { DateFilterType } from '../../../utils/enums';

import './FilterDropdown.scss';

interface CheckHandlerProps {
  choice: string;
  forceCheck?: boolean;
  forceUncheck?: boolean;
  clonedCheckedValues?: IReportFilterString[];
}
interface SelectDeselectAllProps {
  filteredUniqueValues?: IKey;
  selectAll?: boolean;
  deselectAll?: boolean;
}

const returnClonedCheckedValues = (checkedValues: IReportFilterString[] | undefined) => {
  let clonedCheckedValues = cloneDeep(checkedValues);
  if (!clonedCheckedValues) {
    clonedCheckedValues = [];
  }
  return clonedCheckedValues;
};

interface CustomDropdownToggleProps {
  disabled?: boolean;
  children: ReactNode;
  onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {};
}
const CustomDropdownToggle = React.forwardRef(
  (props: CustomDropdownToggleProps, ref: React.Ref<HTMLAnchorElement>) => {
    const toggleElement = (
      <a
        href="#0"
        ref={ref}
        onClick={(e) => {
          e.preventDefault();
          if (!props.disabled) {
            props.onClick(e);
          }
        }}
        className={classNames('d-flex', { disabled: props.disabled })}
        data-boundary="window"
      >
        <span className="material-symbols-sharp FilterDropdown-custom-toggle">filter_list</span>
        {props.children}
      </a>
    );
    if (props.disabled) {
      return (
        <OverlayTrigger
          overlay={
            <Popover id={`column-disabled-tooltip`}>
              <Popover.Content>
                This dropdown is currently disabled because of an active Quick Filter.
              </Popover.Content>
            </Popover>
          }
        >
          {toggleElement}
        </OverlayTrigger>
      );
    }
    return toggleElement;
  },
);

interface FilterDropdownCheckItemProps {
  choice: string;
  checked: boolean;
  checkHandler: (data: CheckHandlerProps) => void;
  currentCheckedValues: IReportFilterString[] | undefined;
}
const FilterDropdownCheckItem = ({
  choice,
  checked,
  checkHandler,
  currentCheckedValues,
}: FilterDropdownCheckItemProps) => {
  const limitedLengthChoice = capStringAtNumChars(choice, 50);
  let clipped = !isEqual(choice, limitedLengthChoice);
  const checkbox = (
    <FormCheckbox id={choice} label={limitedLengthChoice} checked={checked} onChange={() => {}} />
  );
  let content = checkbox;
  if (clipped) {
    content = (
      <OverlayTrigger
        key="dropdown-choice-overlay"
        placement="top"
        overlay={
          <Popover id={`dropdown-choice-tooltip`}>
            <Popover.Content>{choice}</Popover.Content>
          </Popover>
        }
      >
        {checkbox}
      </OverlayTrigger>
    );
  }
  return (
    <RBDropdown.Item
      onClick={() => {
        let clonedCheckedValues = returnClonedCheckedValues(currentCheckedValues);
        checkHandler({ choice, clonedCheckedValues });
      }}
    >
      {content}
    </RBDropdown.Item>
  );
};

interface IDropdownContentProps {
  filterType?: string;
  sortType?: string;
  uniqueValuesInColumn?: IKey;
  checkedValues?: IReportFilterString[];
  checkHandler: (data: CheckHandlerProps) => void;
  selectDeselectAll: (data: SelectDeselectAllProps) => void;
}
const DropdownContent = ({
  filterType,
  sortType,
  uniqueValuesInColumn,
  checkedValues,
  checkHandler,
  selectDeselectAll,
}: IDropdownContentProps) => {
  const [filterInput, setFilterInput] = useState('');
  const [filteredUniqueValues, setFilteredUniqueValues] = useState(uniqueValuesInColumn);

  useEffect(() => {
    const lowerFilter = filterInput.toLocaleLowerCase();
    if (lowerFilter && uniqueValuesInColumn) {
      const updatedFilteredValues: IKey = {};
      Object.keys(uniqueValuesInColumn).forEach((key) => {
        if (key.includes(lowerFilter)) {
          updatedFilteredValues[key] = uniqueValuesInColumn[key];
        }
      });
      setFilteredUniqueValues(updatedFilteredValues);
    } else {
      setFilteredUniqueValues(uniqueValuesInColumn);
    }
  }, [filterInput, uniqueValuesInColumn]);

  return (
    <>
      <InputGroup className="FilterDropdown-filter">
        <span className="material-icons">search</span>
        <Form.Control
          type="text"
          value={filterInput}
          onChange={(event) => setFilterInput(event.target.value)}
        />
      </InputGroup>
      <div className="FilterDropdown-select-all-buttons">
        <span onClick={() => selectDeselectAll({ filteredUniqueValues, selectAll: true })}>
          Select All
        </span>
        <span onClick={() => selectDeselectAll({ filteredUniqueValues, deselectAll: true })}>
          Clear Selections
        </span>
      </div>
      <div className="FilterDropdown-choices">
        {orderBy(Object.keys(filteredUniqueValues || {}), (value) => {
          switch (sortType || filterType) {
            case 'date':
              return value ? new Date(value) : value;
            case 'number':
              return parseFloat(value || '0');
            case 'string':
            default:
              return (value || '').toLowerCase();
          }
        }).map((key) => {
          return (
            <FilterDropdownCheckItem
              choice={filteredUniqueValues ? filteredUniqueValues[key] : ''}
              checked={
                checkedValues?.length &&
                checkedValues.some(
                  (checkedValue) =>
                    checkedValue.comparisonValue.toLocaleLowerCase() === key.toLocaleLowerCase(),
                )
                  ? true
                  : false
              }
              checkHandler={checkHandler}
              key={key}
              currentCheckedValues={checkedValues}
            />
          );
        })}
      </div>
    </>
  );
};

interface IProps {
  dropdownClassName?: string;
  column?: string;
  appliedFilters?: IReportsFilterCategories;
  rows?: Row<any>[];
  setOpenDropdown?: React.Dispatch<React.SetStateAction<string | undefined>>;
  filterType?: string;
  sortType?: string;
  displayString?: string;
  setAppliedFilters?: React.Dispatch<React.SetStateAction<IReportsFilterCategories>>;
}

const FilterDropdown = ({
  appliedFilters,
  column,
  displayString,
  dropdownClassName,
  filterType,
  sortType,
  rows,
  setOpenDropdown,
  setAppliedFilters,
}: IProps) => {
  const uniqueValuesInColumn =
    column && rows ? returnUniqueValuesInColumn(column, rows) : undefined;
  const filterForColumn = appliedFilters && column ? appliedFilters[column] : undefined;

  const isDateFilter = filterTypeIsDate(filterType);
  const stringValuesInColumnFilter = filterForColumn?.stringValues;
  const stringValuesInColumnFilterLength = stringValuesInColumnFilter?.length || 0;

  const [showDropdown, setShowDropdown] = useState(false);
  const [checkedValues, setCheckedValues] = useState(stringValuesInColumnFilter);
  const [startDate, setStartDate] = useState(filterForColumn?.startDate || undefined);
  const [endDate, setEndDate] = useState(filterForColumn?.endDate || undefined);
  const [showDateRangeSelector, setShowDateRangeSelector] = useState<boolean>(false);
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>(
    DateFilterType.allIncludingBlanks,
  );

  const noValuesChecked = checkedValues?.length === 0;
  const hasDateSet = filterForColumn?.startDate && filterForColumn?.endDate;

  const noStringFilterApplied =
    stringValuesInColumnFilterLength === Object.keys(uniqueValuesInColumn || []).length;
  const noDateFilterApplied = !hasDateSet && dateFilterType === DateFilterType.allIncludingBlanks;
  const noFilterApplied = isDateFilter ? noDateFilterApplied : noStringFilterApplied; // No filters applied

  // When the passed appliedFilters changes, need to update the values
  // that are checked in the dropdown.
  useEffect(() => {
    setCheckedValues(stringValuesInColumnFilter);
    setStartDate(filterForColumn?.startDate || undefined);
    setEndDate(filterForColumn?.startDate || undefined);

    setDateFilterType(
      (filterForColumn?.stringValues as DateFilterType) || DateFilterType.allIncludingBlanks,
    ); // Set back to default if no filter applied
  }, [filterForColumn, column, stringValuesInColumnFilter]);

  useEffect(() => {
    if (showDropdown && setOpenDropdown) {
      setOpenDropdown(column);
    }
  }, [showDropdown, column, setOpenDropdown]);

  const toggleHandler = (
    isOpen: boolean,
    event: React.SyntheticEvent<RBDropdown, Event>,
    metadata: { source: 'select' | 'click' | 'rootClose' | 'keydown' },
  ) => {
    if (metadata.source !== 'select') {
      setShowDropdown(isOpen);
    }
  };
  const checkHandler = ({
    choice,
    forceCheck,
    forceUncheck,
    clonedCheckedValues,
  }: CheckHandlerProps) => {
    let indexOfMatchingChoice = -1;

    if (clonedCheckedValues && clonedCheckedValues.length > 0) {
      indexOfMatchingChoice = clonedCheckedValues
        ? clonedCheckedValues.findIndex((clonedCheckedValue) => {
            return (
              clonedCheckedValue.comparisonValue.toLocaleLowerCase() === choice.toLocaleLowerCase()
            );
          })
        : -1;
    }

    // If indexOfMatchingChoice is still -1 at this point,
    // it's not already selected.
    if (indexOfMatchingChoice === -1 && !forceUncheck) {
      clonedCheckedValues?.push({ displayString: choice, comparisonValue: choice });
      // We do not want to uncheck if forceCheck is set to true.
    } else if (indexOfMatchingChoice > -1 && clonedCheckedValues && !forceCheck) {
      clonedCheckedValues.splice(indexOfMatchingChoice, 1);
    }

    if (!forceCheck && !forceUncheck) {
      setCheckedValues(clonedCheckedValues);
    }
  };

  const selectDeselectAll = ({ filteredUniqueValues, selectAll }: SelectDeselectAllProps) => {
    if (!filteredUniqueValues) {
      return;
    }
    const clonedCheckedValues = returnClonedCheckedValues(checkedValues as IReportFilterString[]);
    Object.keys(filteredUniqueValues).forEach((key, index) => {
      checkHandler({
        choice: key,
        forceCheck: selectAll,
        forceUncheck: !selectAll,
        clonedCheckedValues,
      });
    });
    setCheckedValues(clonedCheckedValues);
  };

  const dateChangeHandler = (dates: any) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const cancelActions = () => {
    setCheckedValues(stringValuesInColumnFilter);
    setStartDate(filterForColumn?.startDate);
    setEndDate(filterForColumn?.endDate);
    setShowDropdown(false);
  };

  const handleRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as DateFilterType;

    setDateFilterType(value);
    setShowDateRangeSelector(dateFilterTypeIsRange(value) ? true : false); // Show date range selector if range is selected
  };

  const columnHasQuickFilterApplied = !!(
    appliedFilters &&
    Object.keys(appliedFilters || {}).find((key) => {
      return key === column && !!appliedFilters[key].quickFilterId;
    })
  );

  let dropdownContent = null;

  if (isDateFilter) {
    dropdownContent = (
      <fieldset className="FilterDropdown__DateFilterType">
        <FormInput
          id={`radioInput-${displayString}`}
          fieldName={`radioChoices-${displayString}`}
          label="Date Filter Type"
          type="radio"
          value={dateFilterType}
          radioChoices={[
            {
              label: 'All (Including Blanks)',
              value: DateFilterType.allIncludingBlanks,
            },
            {
              label: 'All (Excluding Blanks)',
              value: DateFilterType.allExcludingBlanks,
            },
            {
              label: '(Blanks)',
              value: DateFilterType.blankOnly,
            },
            {
              label: 'Range',
              value: DateFilterType.range,
            },
          ]}
          onChange={handleRadioChange}
        />

        {!!showDateRangeSelector && (
          <RBDropdown.Item>
            <div>Select Date Range:</div>

            <DatePicker
              selectsRange
              selected={startDate}
              startDate={startDate}
              endDate={endDate}
              placeholderText="No date range selected."
              onChange={dateChangeHandler}
            />
          </RBDropdown.Item>
        )}
      </fieldset>
    );
  } else {
    dropdownContent = (
      <DropdownContent
        filterType={filterType}
        sortType={sortType}
        uniqueValuesInColumn={uniqueValuesInColumn}
        checkedValues={checkedValues as IReportFilterString[]}
        checkHandler={checkHandler}
        selectDeselectAll={selectDeselectAll}
      />
    );
  }

  return (
    <RBDropdown
      className={classNames('FilterDropdown', column, dropdownClassName, {
        'active-filter': !noFilterApplied,
      })}
      show={showDropdown}
      onToggle={toggleHandler}
    >
      <RBDropdown.Toggle
        id={`Filter-${column}`}
        data-boundary="viewport"
        as={CustomDropdownToggle}
        disabled={!!columnHasQuickFilterApplied}
      />
      <RBDropdown.Menu className="FilterDropdown-menu" popperConfig={{ strategy: 'fixed' }}>
        {Object.keys(uniqueValuesInColumn || {}).length > 0 && (
          <>
            <div className="FilterDropdown-menu-content">{dropdownContent}</div>
            <RBDropdown.Divider />
            <div className="FilterDropdown-menu-button-container d-flex justify-content-end">
              {/* When the user clicks cancel, the local filters should be reset to whatever
                the overall applied filter is for this column. */}
              <Button
                className="cancel secondary"
                onClick={() => {
                  cancelActions();
                }}
              >
                Cancel
              </Button>
              {/* When the user clicks filter, the appliedFilters values should be updated
                to whatever is currently checked in the dropdown. */}
              <Button
                className="filter"
                disabled={noValuesChecked}
                onClick={() => {
                  // If the user tries to set a date filter with only one of the two
                  // values entered, behave as if the user hit "cancel" instead.
                  if (
                    filterType === 'date' &&
                    ((startDate && !endDate) || (endDate && !startDate))
                  ) {
                    cancelActions();
                  } else if (appliedFilters && column && setAppliedFilters) {
                    const dateFilterIsRange = dateFilterTypeIsRange(dateFilterType);

                    applyFilter({
                      displayString,
                      checkedValues: isDateFilter ? dateFilterType : checkedValues, // If date filter, use date filter type
                      startDate: dateFilterIsRange ? startDate : undefined, // If date filter is not range, we don't want to set the date
                      endDate: dateFilterIsRange ? endDate : undefined, // If date filter is not range, we don't want to set the date
                      filterType,
                      appliedFilters,
                      column,
                      setAppliedFilters: setAppliedFilters,
                      setShowDropdown: setShowDropdown,
                    });
                  }
                }}
              >
                Filter
              </Button>
            </div>
          </>
        )}
        {(!uniqueValuesInColumn || Object.keys(uniqueValuesInColumn).length < 1) && (
          <RBDropdown.Item className="d-flex justify-content-center">
            No values to filter.
          </RBDropdown.Item>
        )}
      </RBDropdown.Menu>
    </RBDropdown>
  );
};

export default FilterDropdown;
